/**
 * LG cart-drawer recommendations element (feat/lg-cart-recs, plan
 * tasks/features/2026-07-17-lg-cart-recommendations.md).
 *
 * Renders the "You May Also Like" scroller inside the cart drawer by fetching an
 * SRA fragment (sections/lg-cart-recommendations.liquid) from the native product
 * recommendations endpoint and injecting it under this element.
 *
 * HORIZON MORPH SURVIVAL (horizon-mechanics §4.5 / §4.11): this element sits
 * INSIDE the drawer's sole keyed morph target
 * (`data-hydration-key="cart-drawer-inner"`). Every cart mutation re-renders that
 * subtree server-side and morphs it in. The server template (lg-cart-drawer.liquid)
 * emits this element as an EMPTY shell carrying fresh data attributes — so after a
 * morph our injected cards are gone. Two morph shapes must both be handled:
 *   (a) morph REPLACES the element  → connectedCallback fires on the fresh node;
 *   (b) morph UPDATES the element in place → attributeChangedCallback fires (sig
 *       change) and/or the childList observer sees our cards wiped with the sig
 *       unchanged.
 * Both morph-driven paths defer the sync via rAF: morph.js walk() copies
 * attributes (firing attributeChangedCallback synchronously) BEFORE
 * updateChildren() wipes children — a synchronous inject would land mid-morph
 * and be wiped with the childList rescue suppressed (G2 B2).
 *
 * RELATED FILL (owner decision, QA 2026-07-22 — Hydrogen parity): this store's
 * S&D complementary rules are manually PINNED and short (e.g. 2 products per
 * seed, `pr_prod_strat=pinned`), and the unconditional in-cart trim eats them
 * (live: a 12-item cart showed 1 card). Hydrogen never had this because it
 * merged COMPLEMENTARY + RELATED. So: when `data-intent` is `complementary`
 * and the post-trim card count is below the display target
 * (min(max(3, paidCount), limit)), a SECOND fetch of the same URL with
 * `intent=related` fills the deck — deduped by product id against the
 * already-shown cards AND the in-cart ids, complementary cards keep priority
 * order first. The fill fires ONLY when short; `related` intent stays a pure
 * single-fetch path.
 *
 * CACHE / RE-INJECT MODEL: raw fragments are cached under their OWN request
 * URLs (complementary and related each get an entry); the merged deck is
 * DERIVED per render, never cached as a third entry. The childList rescue
 * (cards wiped, sig unchanged) re-RUNS the whole assemble pipeline via #sync —
 * with both fragments already cached that is a no-network re-derive, so the
 * rescue re-injects the MERGED result, not just the primary fragment.
 *
 * The MODULE is loaded from lg-cart-drawer.liquid, OUTSIDE the morph target,
 * next to lg-cart-samples.js — a <script> emitted inside the morphed fragment is
 * inert (DOMParser marks parser-created scripts "already started"; Gate-2 review
 * BLOCKER 1, cart-drawer). Morph-inserted <lg-cart-recs> instances upgrade
 * against this pre-registered definition.
 *
 * §4.1 defensive init: connectedCallback may run before later DOM exists, but
 * this element only touches its own subtree, so no external-DOM defer is needed
 * beyond the rAF morph-settle defer above.
 */

// Fragment cache keyed by request URL, shared across element re-creations so a
// morph that re-creates the element re-injects instantly from cache.
/** @type {Map<string, string>} */
const fragmentCache = new Map();

class LgCartRecs extends HTMLElement {
  static observedAttributes = ['data-cart-sig'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {MutationObserver | null} */
  #childObserver = null;

  /** Signature currently reflected by our injected DOM (null = nothing injected). */
  #renderedSig = null;

  /** Guards against re-entrant work while we inject (childList observer would fire). */
  #injecting = false;

  connectedCallback() {
    // Watch our own children: a morph can wipe them while leaving the element in
    // place with an unchanged signature — re-derive + re-inject when that happens.
    this.#childObserver = new MutationObserver(() => this.#onChildMutation());
    this.#childObserver.observe(this, { childList: true });

    // Defer to the next frame so a morph that created us has fully settled before
    // we read attributes / inject (avoids racing the section-renderer's own DOM ops).
    requestAnimationFrame(() => {
      if (this.isConnected) this.#sync();
    });
  }

  disconnectedCallback() {
    this.#childObserver?.disconnect();
    this.#childObserver = null;
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  /** @param {string} name @param {string | null} oldValue @param {string | null} newValue */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-cart-sig') return;
    // Fires on in-place morph updates. Skip the initial set (oldValue null) —
    // connectedCallback's rAF handles first paint.
    if (oldValue === null || oldValue === newValue) return;
    // Defer past the synchronous morph stack (G2 B2): morph.js walk() runs
    // updateNode()→copyAttributes — which fires this callback synchronously on
    // the sig copy — BEFORE updateChildren() wipes our injected cards against
    // the empty shell and re-copies the shell's `hidden` (it follows
    // data-cart-sig in the template attribute order). A synchronous #sync would
    // inject MID-morph, get wiped by updateChildren, and the childList rescue
    // would be skipped because #injecting is still true → row permanently
    // empty+hidden. rAF runs after the morph unwinds — same rationale as the
    // connectedCallback defer above.
    requestAnimationFrame(() => {
      if (this.isConnected) this.#sync();
    });
  }

  #onChildMutation() {
    if (this.#injecting) return;
    // A morph wiped our cards but the signature did not change — re-derive the
    // merged deck from the URL-keyed fragment caches (no network with warm
    // caches) and re-inject. Going through #sync (rather than injecting one
    // cached fragment directly) is what makes the rescue re-inject the MERGED
    // complementary+related result.
    const sig = this.dataset.cartSig || '';
    if (this.#renderedSig === sig && !this.#hasCards() && fragmentCache.has(this.#url())) {
      this.#renderedSig = null; // force #sync past its already-rendered no-op
      this.#sync();
    }
  }

  /** True once at least one rec card is present in our subtree. */
  #hasCards() {
    return this.querySelector('.cart-rec') !== null;
  }

  /**
   * Recommendations endpoint URL for a given intent (seed/limit from attributes).
   * @param {string} intent
   */
  #urlFor(intent) {
    const { url, seedId, limit } = this.dataset;
    if (!url || !seedId) return '';
    const base = url.includes('?') ? url : `${url}?`;
    const sep = base.endsWith('?') ? '' : '&';
    return `${base}${sep}section_id=lg-cart-recommendations&product_id=${seedId}&limit=${limit || 10}&intent=${intent}`;
  }

  /** Primary request URL for the configured intent. */
  #url() {
    return this.#urlFor(this.dataset.intent || 'related');
  }

  /**
   * Reconcile the injected DOM with the current attributes. No-op when the
   * current signature is already rendered.
   */
  #sync() {
    const sig = this.dataset.cartSig || '';
    const url = this.#url();

    // No seed (empty cart / all-sample cart guard upstream) → nothing to show.
    if (!url) {
      this.#clear();
      return;
    }

    // Already showing this signature's cards — nothing to do (avoids morph churn).
    if (this.#renderedSig === sig && this.#hasCards()) return;

    this.#load(url, sig);
  }

  /**
   * Fetch (or read cached) fragments, assemble the deck — unconditional in-cart
   * trim (G2 N1), display-target slice, related fill for short complementary
   * (owner 2026-07-22) — and inject.
   * @param {string} url @param {string} sig
   */
  async #load(url, sig) {
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const primaryText = await this.#getFragment(url, controller.signal);
      if (controller.signal.aborted) return;
      if (primaryText === null) {
        this.#clear();
        return;
      }
      // Stale guard: a newer morph may have changed the seed/intent mid-flight.
      if (!this.isConnected || this.#url() !== url) return;

      const primaryRegion = this.#parseRegion(primaryText);
      if (!primaryRegion) {
        this.#clear();
        return;
      }

      // In-cart exclusion set — UNCONDITIONAL trim (G2 N1): the URL-keyed cache
      // outlives cart membership while data-in-cart-ids is fresh on every morph.
      // Seeding `seen` with the in-cart ids makes the collector both the in-cart
      // trim and the cross-fragment dedup.
      const seen = new Set((this.dataset.inCartIds || '').split(',').filter(Boolean));

      // Display target: min( max(3, paidLinesCount), limit ) — owner Q4 formula,
      // computed client-side from the drawer-fresh attributes. The server slice
      // uses the same formula when its `cart` global is populated; the client
      // cap is authoritative because its inputs are always current.
      const paidCount = Number(this.dataset.paidCount || '0') || 0;
      const limit = Number(this.dataset.limit || '10') || 10;
      const target = Math.min(Math.max(3, paidCount), limit);

      /** @type {Element[]} */
      const deck = [];
      this.#collectCards(primaryRegion, deck, seen, target);

      // RELATED FILL (owner 2026-07-22, Hydrogen parity): pinned S&D
      // complementary lists are short and the in-cart trim eats them — top up
      // from the related feed, deduped, complementary keeps priority order.
      // Fires ONLY when short, so the common related-only path stays single-fetch.
      let fillRegion = null;
      if (deck.length < target && (this.dataset.intent || 'related') === 'complementary') {
        const fillText = await this.#getFragment(this.#urlFor('related'), controller.signal);
        if (controller.signal.aborted) return;
        if (!this.isConnected || this.#url() !== url) return;
        if (fillText !== null) {
          fillRegion = this.#parseRegion(fillText);
          if (fillRegion) this.#collectCards(fillRegion, deck, seen, target);
        }
      }

      // Host structure (head + scroller) comes from whichever region actually
      // rendered one — an empty complementary fragment has NO head/scroller, so
      // a fill-only deck borrows the related fragment's structure.
      const hostRegion = primaryRegion.querySelector('.cart-recs__scroll')
        ? primaryRegion
        : fillRegion;
      this.#render(deck, hostRegion, sig);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('lg-cart-recs: failed to load recommendations', error);
      this.#clear();
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }

  /**
   * Return the raw fragment for a URL — from the module cache, else fetched and
   * cached. null = HTTP failure (caller decides what to do).
   * @param {string} url @param {AbortSignal} signal
   * @returns {Promise<string | null>}
   */
  async #getFragment(url, signal) {
    const cached = fragmentCache.get(url);
    if (cached !== undefined) return cached;

    const response = await fetch(url, { signal });
    if (!response.ok) return null;

    const text = await response.text();
    fragmentCache.set(url, text);
    return text;
  }

  /**
   * Parse an SRA fragment and return its `[data-lg-recs]` region (or null).
   * ALWAYS inspect the fragment as the browser will PARSE it, never as response
   * text (parse-time reparenting can diverge from the source string —
   * horizon-mechanics live-evidence lesson). DOMParser mirrors the section
   * renderer's own parse.
   * @param {string} html
   */
  #parseRegion(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('[data-lg-recs]');
  }

  /**
   * Append a region's cards to the deck, skipping product ids already seen
   * (in-cart or already picked), stopping at the display target.
   * @param {Element} region @param {Element[]} deck
   * @param {Set<string>} seen @param {number} target
   */
  #collectCards(region, deck, seen, target) {
    for (const card of Array.from(region.querySelectorAll('.cart-recs__scroll > .cart-rec'))) {
      if (deck.length >= target) return;
      const pid = /** @type {HTMLElement} */ (card).dataset.productId || '';
      if (!pid || seen.has(pid)) continue;
      seen.add(pid);
      deck.push(card);
    }
  }

  /**
   * Inject the assembled deck under this element. Empty deck → row absent
   * (hidden, no head). Non-empty → the host region's head + scroller are moved
   * in, with the scroller's children replaced by the deck.
   * @param {Element[]} deck @param {Element | null} hostRegion @param {string} sig
   */
  #render(deck, hostRegion, sig) {
    const scroller = hostRegion?.querySelector('.cart-recs__scroll');

    this.#injecting = true;
    if (deck.length === 0 || !hostRegion || !scroller) {
      this.replaceChildren();
      this.hidden = true;
    } else {
      scroller.replaceChildren(...deck);
      this.replaceChildren(...Array.from(hostRegion.childNodes));
      this.hidden = false;
    }
    // Empty-but-successful renders still record the sig so we don't re-derive in
    // a loop; only errors (#clear) reset it to null.
    this.#renderedSig = sig;
    // Release the guard after the mutation record for this injection is delivered.
    requestAnimationFrame(() => {
      this.#injecting = false;
    });
  }

  #clear() {
    this.#injecting = true;
    this.replaceChildren();
    this.hidden = true;
    this.#renderedSig = null;
    requestAnimationFrame(() => {
      this.#injecting = false;
    });
  }
}

if (!customElements.get('lg-cart-recs')) {
  customElements.define('lg-cart-recs', LgCartRecs);
}
